/**
 * @module app.core
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { INotesConfig } from 'app/core/models/essette-interfaces';
import { NoteService } from 'app/core/services/note-service';
import { Toaster } from 'app/shared/lib/toaster';
import { bindable } from 'aurelia-typed-observable-plugin';
import { forceNull } from 'app/shared/lib/utilities';
import { getReference } from 'app/shared/models/essette-types';
import { apiCall } from 'app/shared/lib/essette-api';
import { I18N } from 'aurelia-i18n';
import * as Core from 'app/core/models/essette-api';

@autoinject
export class SaveNoteCategory extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.toView })
    protected noteCategory: Core.NoteCategory = {};

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    protected notesConfig?: INotesConfig;

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected noteFlags: Core.NoteFlag[] = [];
    protected selectedNoteFlags: Core.NoteFlag[] = [];

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly toaster: Toaster,
        private readonly service: NoteService,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        // verify that component is properly configured
        if(!this.notesConfig) throw new Error('Bindable property [notes-config] was not set');
        if(!this.notesConfig?.serviceRootUrl) throw new Error('[serviceRootUrl] was not configured on bindable property [notes-config]');
        this.service.configure(this.notesConfig);
    }

    ////****************** Events ***************************************************////
    protected noteCategoryChanged(): void {
        this.selectedNoteFlags = this.noteCategory?.flags ?? [];
    }

    ////****************** Functions ************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public reset(): void {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        return this.saveNoteCategory();
    }

    protected constructSaveObject(): Core.NoteCategory {
        const saveNoteCategory: Core.NoteCategory = {
            id: this.noteCategory.id,
            description: this.noteCategory.description,
            code: forceNull(this.noteCategory.code),
            flags: []
        }

        this.selectedNoteFlags.forEach(noteFlag => {
            const noteFlagReference = getReference(noteFlag);
            if(noteFlagReference) saveNoteCategory.flags!.push(noteFlagReference);
        });

        return saveNoteCategory;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async saveNoteCategory(): Promise<boolean> {
        let isSuccessful = false;
        let duplicateCodeError = false;
        try {
            this.state.beginSave();
            const response = await this.service.saveNoteCategory(this.constructSaveObject());
            isSuccessful = response.isSuccessful;
            return isSuccessful;
        } catch(error) {
            if(error.status?.errors) duplicateCodeError = error.status.errors.some((err: any) => err.code === 'property.constraintViolated' && err.property === 'code');
            throw error;
        } finally {
            if(duplicateCodeError) {
                if(this.noteCategory.id) {
                    this.toaster.error().message(this.i18n.tr('ui:messages.duplicate.update_error', { what: this.i18n.tr('core:note.category.code.$'), item: this.i18n.tr('core:note.category.$long') })).show();
                } else {
                    this.toaster.error().message(this.i18n.tr('ui:messages.duplicate.create_error', { what: this.i18n.tr('core:note.category.code.$'), item: this.i18n.tr('core:note.category.$long') })).show();
                }
                this.toaster.error().message(this.i18n.tr('ui:messages.warnings.inUse', { what: this.i18n.tr('core:note.category.code.$') })).show();
            } else {
                if(this.noteCategory.id) {
                    this.toaster.standard.updated('core:note.category.$long', isSuccessful);
                } else {
                    this.toaster.standard.created('core:note.category.$long', isSuccessful);
                }
            }
            this.state.endSave();
        }
    }
}
